import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../components/Layout';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { useAuth } from '../context/AuthContext';
import { ThumbsUp, ThumbsDown, Plus, Music, Loader2, TrendingUp, User } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = ['All', 'seasonal', 'thematic', 'genre'];

const Playlists = () => {
  const { isAuthenticated, token } = useAuth();
  const [playlists, setPlaylists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newPlaylist, setNewPlaylist] = useState({ name: '', description: '', category: 'thematic' });

  useEffect(() => {
    fetchPlaylists();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCategory]);

  const fetchPlaylists = async () => {
    try {
      setLoading(true);
      const params = { sort_by: 'score' };
      if (selectedCategory !== 'All') params.category = selectedCategory;
      const response = await axios.get(`${API}/playlists`, { params });
      setPlaylists(response.data);
    } catch (error) {
      console.error('Failed to fetch playlists:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleVote = async (playlistId, vote) => {
    if (!isAuthenticated) {
      toast.error('Please login to vote');
      return;
    }
    try {
      await axios.post(
        `${API}/playlists/${playlistId}/vote`,
        { vote },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPlaylists(
        playlists.map((p) =>
          p.id === playlistId
            ? {
                ...p,
                score: p.score + vote,
                upvotes: vote === 1 ? p.upvotes + 1 : p.upvotes,
                downvotes: vote === -1 ? p.downvotes + 1 : p.downvotes,
              }
            : p
        )
      );
      toast.success('Vote recorded!');
    } catch (error) {
      toast.error('Failed to vote');
    }
  };

  const handleCreatePlaylist = async (e) => {
    e.preventDefault();
    if (!newPlaylist.name) {
      toast.error('Please enter a playlist name');
      return;
    }
    try {
      await axios.post(`${API}/playlists`, newPlaylist, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Playlist created!');
      setDialogOpen(false);
      setNewPlaylist({ name: '', description: '', category: 'thematic' });
      fetchPlaylists();
    } catch (error) {
      toast.error('Failed to create playlist');
    }
  };

  return (
    <Layout>
      <div className="py-6 md:py-8 px-4 md:px-8 pb-40">
        <div className="container mx-auto max-w-6xl">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-[#FFE500] font-bold mb-2">Community</p>
              <h1 className="text-3xl md:text-5xl font-extrabold text-white">Worship Playlists</h1>
              <p className="text-[#A0A0A0] mt-2">Vote for your favorites. Top 10 featured weekly!</p>
            </div>
            {isAuthenticated && (
              <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogTrigger asChild>
                  <button className="pill-btn pill-btn-active flex items-center gap-2" data-testid="create-playlist-btn">
                    <Plus className="h-4 w-4" />
                    Create Playlist
                  </button>
                </DialogTrigger>
                <DialogContent className="bg-[#16181D] border-[#1E2028] rounded-3xl text-white">
                  <DialogHeader>
                    <DialogTitle className="text-2xl font-extrabold">Create New Playlist</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleCreatePlaylist} className="space-y-4">
                    <div>
                      <label className="block text-sm font-semibold text-[#A0A0A0] mb-2">Name</label>
                      <input
                        value={newPlaylist.name}
                        onChange={(e) => setNewPlaylist({ ...newPlaylist, name: e.target.value })}
                        placeholder="Playlist name"
                        className="input-modern"
                        data-testid="playlist-name-input"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-[#A0A0A0] mb-2">Description</label>
                      <textarea
                        value={newPlaylist.description}
                        onChange={(e) => setNewPlaylist({ ...newPlaylist, description: e.target.value })}
                        placeholder="Describe your playlist..."
                        className="input-modern resize-none"
                        rows={3}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-[#A0A0A0] mb-2">Category</label>
                      <div className="flex gap-2">
                        {['seasonal', 'thematic', 'genre'].map((cat) => (
                          <button
                            type="button"
                            key={cat}
                            onClick={() => setNewPlaylist({ ...newPlaylist, category: cat })}
                            className={`pill-btn capitalize ${newPlaylist.category === cat ? 'pill-btn-active' : 'pill-btn-inactive'}`}
                          >
                            {cat}
                          </button>
                        ))}
                      </div>
                    </div>
                    <button
                      type="submit"
                      className="w-full py-3 rounded-full bg-[#FFE500] text-black font-bold hover:bg-[#F0D800] transition-all"
                      data-testid="submit-playlist-btn"
                    >
                      Create Playlist
                    </button>
                  </form>
                </DialogContent>
              </Dialog>
            )}
          </div>

          {/* Category Pills */}
          <div className="flex gap-2 overflow-x-auto pb-4 mb-4 scrollbar-hide" data-testid="category-filters">
            {CATEGORIES.map((cat) => (
              <button
                key={cat}
                onClick={() => setSelectedCategory(cat)}
                className={`pill-btn capitalize flex-shrink-0 ${
                  selectedCategory === cat ? 'pill-btn-active' : 'pill-btn-inactive'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-24">
              <Loader2 className="h-8 w-8 animate-spin text-[#FFE500]" />
            </div>
          ) : playlists.length > 0 ? (
            <div className="space-y-3">
              {playlists.map((playlist, index) => (
                <div
                  key={playlist.id}
                  className="bg-[#16181D] rounded-2xl p-5 flex items-center gap-4 hover:bg-[#1E2028] transition-colors"
                  data-testid={`playlist-${playlist.id}`}
                >
                  <div className="w-10 text-center flex-shrink-0">
                    <span className={`text-2xl font-extrabold ${index < 3 ? 'text-[#FFE500]' : 'text-[#666]'}`}>
                      {index + 1}
                    </span>
                  </div>

                  <div className="flex flex-col items-center gap-0 flex-shrink-0">
                    <button
                      onClick={() => handleVote(playlist.id, 1)}
                      className="p-1.5 text-[#A0A0A0] hover:text-[#FFE500] transition-colors"
                      data-testid={`upvote-${playlist.id}`}
                    >
                      <ThumbsUp className="h-4 w-4" />
                    </button>
                    <span className={`text-sm font-bold ${playlist.score > 0 ? 'text-[#FFE500]' : playlist.score < 0 ? 'text-red-500' : 'text-[#A0A0A0]'}`}>
                      {playlist.score}
                    </span>
                    <button
                      onClick={() => handleVote(playlist.id, -1)}
                      className="p-1.5 text-[#A0A0A0] hover:text-red-500 transition-colors"
                      data-testid={`downvote-${playlist.id}`}
                    >
                      <ThumbsDown className="h-4 w-4" />
                    </button>
                  </div>

                  <div className="flex-1 min-w-0">
                    <h3 className="text-lg font-bold text-white truncate">{playlist.name}</h3>
                    {playlist.description && (
                      <p className="text-[#A0A0A0] text-sm line-clamp-1 mb-1.5">{playlist.description}</p>
                    )}
                    <div className="flex items-center flex-wrap gap-3 text-xs text-[#666]">
                      <span className="flex items-center gap-1">
                        <User className="h-3 w-3" />
                        {playlist.creator_name}
                      </span>
                      <span className="flex items-center gap-1">
                        <Music className="h-3 w-3" />
                        {playlist.songs?.length || 0} songs
                      </span>
                      <span className="capitalize bg-[#0A0B0F] px-2.5 py-0.5 rounded-full">{playlist.category}</span>
                    </div>
                  </div>

                  {index < 3 && (
                    <div className="hidden sm:flex items-center gap-1 text-[#FFE500] flex-shrink-0">
                      <TrendingUp className="h-4 w-4" />
                      <span className="text-xs font-bold uppercase">Trending</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-24 bg-[#16181D] rounded-3xl">
              <Music className="h-16 w-16 mx-auto mb-4 text-[#666]" />
              <h3 className="text-xl font-bold text-white mb-2">No Playlists Yet</h3>
              <p className="text-[#A0A0A0] mb-6">Be the first to create a worship playlist!</p>
              {isAuthenticated ? (
                <button onClick={() => setDialogOpen(true)} className="pill-btn pill-btn-active">
                  Create First Playlist
                </button>
              ) : (
                <Link to="/login">
                  <button className="pill-btn pill-btn-active">Login to Create</button>
                </Link>
              )}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default Playlists;
